<!--

	Displays Vimeo videos in the embedded player providing an API for controlling it

	Note that while this player is mostly reactive, you should not assume that
	all props can be updated in all situations. In particular, you should make
	sure that it does not get recylced by different displays, e.g., by adding a
	:key to it in the parent component.

-->
<template>
	<div ref="vimeoContainer" class="mia-vimeoPlayerContainer">
		<div ref="overlayPopover" popover="manual" class="mia-vimeoPlayerOverlayPopover">
			<banner
				v-if="maxSpeedLimitEnabled"
				class="mia-vimeoPlayerBanner"
				:show="maxSpeedLimitWarningShown"
				:title-tr="{ key: 'vimeo_player_limit_message_title', speed: maxSpeedLimit ?? 1.25 }"
				state="information"
				:mode="bannerMode"
				:icon-src="handWavingIcon"
				:animate="true"
				:auto-hide-duration-ms=3000
				@update:show="onMaxSpeedLimitMessageShowChanged"
			/>
		</div>
		<div class="mia-vimeoPlayer">
		<iframe
			ref="vimeoPlayer"
			:src="videoSrc"
			:allowfullscreen="true"
			data-ready="true"
			allow="autoplay; encrypted-media"
			:class="isVerticalVideo ? 'mia-vertical' : 'mia-horizontal'"
		/>
		</div>
		<div class="mia-vimeoPlayerToast">
			<banner
				v-if="maxSpeedLimitEnabled"
				class="mia-vimeoPlayerBanner"
				:show="maxSpeedLimitWarningShown"
				:title-tr="{ key: 'vimeo_player_limit_message_title', speed: maxSpeedLimit ?? 1.25 }"
				state="information"
				:mode="bannerMode"
				:icon-src="handWavingIcon"
				show-close-button
				:animate="true"
				:auto-hide-duration-ms="3000"
				@update:show="onMaxSpeedLimitMessageShowChanged"
			/>
		</div>
	</div>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch, toNative } from "vue-facing-decorator";
import type Player from "@vimeo/player";
import { markRaw, nextTick } from "vue";
import pDefer, { type DeferredPromise } from "p-defer";
import Log from "../../utils/Log";
import Banner, { type BannerMode } from "@/common/Banner.vue";
import HandWavingIcon from "@theme/icons/hand_waving.svg";

type TimeEvent = { seconds: number };
type VolumeChangeEvent = { volume: number };
type PlayerErrorEvent = { name: string, message: string, method: string };

const VIMEO_READY_LOG_TIMEOUT_MS = 10000;

@Component({
	components: { Banner },
	emits: ["update:time", "update:volume", "pause", "play", "loaded", "ended"],
})
export class VimeoPlayer extends Vue {
	readonly handWavingIcon = HandWavingIcon;

	@Prop({ required: true }) videoSrc!: string;
	@Prop({ required: false, default: false }) protected enableGoogleTracking!: boolean;
	@Prop({ required: false, default: false }) protected autoPlay!: boolean;
	@Prop({ required: false, default: false }) darkMode!: boolean;
	@Prop({ required: false, default: null }) protected volume!: number | null;
	@Prop({ required: false, default: null }) protected textTrack!: string | null;
	@Prop({ required: false, default: null }) protected time!: number | null;
	@Prop({ required: false, default: false }) protected isPlaying!: boolean;
	@Prop({ required: false, default: false }) isVerticalVideo!: boolean;
	@Prop({ required: false, default: false }) maxSpeedLimitEnabled!: boolean;
	@Prop({ required: false, default: null }) maxSpeedLimit!: number | null;
	@Prop({ required: false, default: false }) logVimeoPlayerErrors!: boolean;

	protected player!: DeferredPromise<Player>;

	// The time stamp we reported most recently in a update:time event.
	private timeupdate: number | null = null;

	private duration = 0;
	private fullscreenHandler: (() => void) | null = null;
	private loggedVimeoErrors: Set<string> = new Set();
	private isUnmounted = false;
	private vimeoReadyState: "pending" | "ready" | "failed" = "pending";
	private readyTimeoutId: ReturnType<typeof setTimeout> | null = null;

	maxSpeedLimitWarningShown = false;
	get bannerMode(): BannerMode {
		return this.darkMode ? "dark" : "light";
	}

	created() {
		this.player = pDefer<Player>();
		this.player.promise.catch(() => {});
	}

	private getErrorType(error: unknown, fallbackType: string): string {
		if (error instanceof Error && error.name) {
			return error.name;
		}
		return fallbackType;
	}

	private async logVimeoErrorOnce(errorName: string, baseMessage: string, error: unknown | undefined): Promise<void> {
		if (!this.logVimeoPlayerErrors || this.loggedVimeoErrors.has(errorName as string)) {
			return;
		}
		let message = baseMessage;
		if(error){
			message += `: ${error instanceof Error ? error.message : String(error)}`;
		}
		this.loggedVimeoErrors.add(errorName);
		Log.error(message);
	}

	private startReadyTimeout(): void {
		this.readyTimeoutId = setTimeout(() => {
			if (this.vimeoReadyState === "pending") {
				void this.logVimeoErrorOnce(
					"ReadyTimeout",
					`Vimeo player did not become ready within ${VIMEO_READY_LOG_TIMEOUT_MS} ms (the video may be slow to load or unable to connect)`,
					undefined
				);
			}
		}, VIMEO_READY_LOG_TIMEOUT_MS);
	}

	private clearReadyTimeout(): void {
		if (this.readyTimeoutId !== null) {
			clearTimeout(this.readyTimeoutId);
			this.readyTimeoutId = null;
		}
	}

	beforeUnmount(): void {
		this.isUnmounted = true;
		this.clearReadyTimeout();
		if (this.vimeoReadyState === "pending") {
			void this.logVimeoErrorOnce(
				"UnmountedBeforeReady",
				`Vimeo player was unmounted before it became ready`,
				undefined
			);
		}
		if (this.fullscreenHandler) {
			document.removeEventListener("fullscreenchange", this.fullscreenHandler);
		}
	}

	async mounted(): Promise<void> {
		try {
			this.fullscreenHandler = () => {
				const popover = this.$refs.overlayPopover;
				if (!(popover instanceof HTMLElement)) {
					return;
				}

				if (document.fullscreenElement) {
					try { popover.showPopover(); } catch { }
				} else {
					try { popover.hidePopover(); } catch { }
				}
			};
			document.addEventListener("fullscreenchange", this.fullscreenHandler);

			const iframe = this.$refs.vimeoPlayer;
			if (!(iframe instanceof HTMLIFrameElement)) {
				throw new Error("VimeoPlayer ref is not an HTMLIFrameElement.");
			}

			const Player = (await import("@vimeo/player")).default;

			if (this.isUnmounted) {
				return;
			}

			const player = markRaw(new Player(iframe));

			this.startReadyTimeout();
			await player.ready();
			this.vimeoReadyState = "ready";
			this.clearReadyTimeout();
			this.$emit("loaded");
			this.player.resolve(player);
			this.duration = await this.getDuration();

			await this.onTimeChange();
			await this.onVolumeChange();
			await this.onTextTrackChange();

			player.on("timeupdate", (data: any) => {
				this.timeupdate = data.seconds;
				this.$emit("update:time", this.timeupdate, this.duration);
			});
			player.on("ended", () => this.$emit("ended"));
			player.on("volumechange", (event: any) => this.$emit("update:volume", event.volume));
			player.on("pause", () => this.$emit("pause"));
			player.on("play", () => this.$emit("play"));
			player.on("error", (error: PlayerErrorEvent) => {
				void this.logVimeoErrorOnce(
					error.name,
					`Vimeo player error: [${error.name}] ${error.message} (method: ${error.method})`,
					undefined
				);
			});

			if (this.autoPlay) {
				await this.play();
			}

			if (this.maxSpeedLimitEnabled && this.maxSpeedLimit) {
				player.on("playbackratechange", (data: any) => {
					if (data.playbackRate > this.maxSpeedLimit!) {
						void this.showMaxSpeedLimitWarning();
						player.setPlaybackRate(this.maxSpeedLimit!);
					}
				});
			}
		}
		catch (error: unknown) {
			this.vimeoReadyState = "failed";
			this.clearReadyTimeout();
			this.$emit("loaded");
			this.player.reject(error);
			await this.logVimeoErrorOnce(
				"PlayerLoadFailure",
				`Vimeo player failed to load`,
				error
			);
		}
	}

	private async showMaxSpeedLimitWarning(): Promise<void> {
		this.maxSpeedLimitWarningShown = true;
	}

	onMaxSpeedLimitMessageShowChanged(show: boolean): void {
		this.maxSpeedLimitWarningShown = show;
	}

	public async getDuration(): Promise<number> {
		return (await this.player.promise).getDuration();
	}

	// Implementation note: This is not a good pattern. Instead there should be a
	// reactive .syncable "state" prop that controls whether the player is
	// currently paused or playing. However, we need this interface to support
	// some legacy code paths that we did not want to risk to break in the latest
	// round of changes here.
	public async play(): Promise<void> {
		try {
			await (await this.player.promise).play();

			if (this.enableGoogleTracking && typeof gtag === "function") {
				gtag("event", "play", {
					event_category: "video",
					event_label: this.videoSrc,
				});
			}
		}
		catch (error: unknown) {
			await this.logVimeoErrorOnce(
				`play:${this.getErrorType(error, "PlayFailure")}`,
				`Vimeo player play() failed:`,
				error
			);
		}
	}

	public async pause(): Promise<void> {
		try {
			await (await this.player.promise).pause();
		}
		catch (error: unknown) {
			await this.logVimeoErrorOnce(
				`pause:${this.getErrorType(error, "PauseFailure")}`,
				`Vimeo player pause() failed`,
				error
			);
		}
	}

	// Implementation note: This method exists to support some legacy code paths.
	// It should not be used anymore. Instead the "time" should be set directly.
	public async setVideoProgress(seconds: number): Promise<void> {
		try {
			await (await this.player.promise).setCurrentTime(seconds);
		}
		catch (error: unknown) {
			await this.logVimeoErrorOnce(
				`setVideoProgress:${this.getErrorType(error, "SetVideoProgressFailure")}`,
				`Vimeo player setVideoProgress(${seconds}) failed`,
				error
			);
		}
	}

	public async unload(): Promise<void> {
		try {
			await (await this.player.promise).ready();
			await (await this.player.promise).unload();
		}
		catch (error: unknown) {
			await this.logVimeoErrorOnce(
				`unload:${this.getErrorType(error, "UnloadFailure")}`,
				`Vimeo player unload() failed`,
				error
			);
		}
	}

	@Watch("time")
	private async onTimeChange() {
		if (this.time == null) {
			return;
		}
		if (this.time !== this.timeupdate) {
			this.timeupdate = this.time;
			try {
				// We must not set the time to exactly 0, see
				// https://github.com/vimeo/player.js/issues/178 (which is closed but not
				// fixed as of early 2025.)
				await (await this.player.promise).setCurrentTime(Math.max(this.time, Number.EPSILON));
			}
			catch (error: unknown) {
				await this.logVimeoErrorOnce(
					`setCurrentTime:${this.getErrorType(error, "SetCurrentTimeFailure")}`,
					`Vimeo player setCurrentTime() failed`,
					error
				);
			}
		}
	}

	@Watch("volume")
	private async onVolumeChange() {
		if (this.volume == null) {
			return;
		}
		try {
			await (await this.player.promise).setVolume(this.volume);
		}
		catch (error: unknown) {
			await this.logVimeoErrorOnce(
				`setVolume:${this.getErrorType(error, "SetVolumeFailure")}`,
				`Vimeo player setVolume() failed`,
				error
			);
		}
	}

	@Watch("textTrack")
	private async onTextTrackChange() {
		if (this.textTrack == null) {
			return;
		}
		try {
			await (await this.player.promise).enableTextTrack(this.textTrack);
		}
		catch (error: unknown) {
			await this.logVimeoErrorOnce(
				`enableTextTrack:${this.getErrorType(error, "EnableTextTrackFailure")}`,
				`Vimeo player enableTextTrack("${this.textTrack}") failed`,
				error
			);
		}
	}

	@Watch("isPlaying")
	private async onIsPlayingChange() {
		if (this.isPlaying) {
			await this.play();
			return;
		}
		await this.pause();
	}
}

export default toNative(VimeoPlayer);
</script>
<style lang="scss">
.mia-vimeoPlayerContainer {
	position: relative;
	width: 100%;
	display: block;

	&:fullscreen {
		background: theme.$mia-color-darkBlack;

		.mia-vimeoPlayer {
			height: 100%;
		}

		iframe {
			height: 100%;
		}
	}
}

.mia-vimeoPlayerToast {
	position: absolute;
	top: theme.$mia-margin-size;
	right: theme.$mia-margin-size;
	width: theme.$mia-vimeo-player-banner-width;
	max-width: calc(100% - #{theme.$mia-margin-size-large});
	overflow: hidden;
	pointer-events: none;

	> * {
		pointer-events: auto;
	}
}

.mia-vimeoPlayerOverlayPopover {
	position: fixed;
	inset: auto;
	top: theme.$mia-margin-size;
	right: theme.$mia-margin-size;
	width: theme.$mia-vimeo-player-banner-width;
	max-width: calc(100% - #{theme.$mia-margin-size-large});
	margin: 0;
	border: none;
	padding: 0;
	background: transparent;
	overflow: hidden;
}

.mia-vimeoPlayerBanner .mia-Banner {
	box-shadow: theme.$mia-shadow-box;
}

.mia-vimeoPlayer {
	width: 100%;
	iframe {
		border: none;
		width: 100%;
		display: block;

		&.mia-vertical {
			aspect-ratio: 9 / 16;
		}

		&.mia-horizontal {
			aspect-ratio: 16 / 9;
		}
	}
}
</style>
